// src\components\FormSuccess.js

import React, { useState, useEffect } from "react";
import { 
  CircularProgress, 
  Button, 
  Typography, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  LinearProgress,
  Paper
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import localApi from "../externalApi/local.js";
import { useLocation } from "react-router-dom";

// Логотип компании из файла favicon.svg
const CompanyLogo = () => (
  <img src="/favicon.svg" alt="Логотип компании" width="32" height="32" />
);

// Тема с фирменными цветами
const themeColors = {
  primary: '#af3831',
  primaryLight: '#c44940',
  primaryDark: '#963029',
  hover: 'rgba(175, 56, 49, 0.04)',
  focus: 'rgba(175, 56, 49, 0.12)'
};

const FormSuccess = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const user_id = queryParams.get('user_id');
  const transaction_id = queryParams.get('transaction_id');
  // Добавляем параметр для определения бесплатного тестирования
  const free_trial = queryParams.get('free_trial') === 'true';
  
  // Состояния для управления отображением процесса
  const [activeStep, setActiveStep] = useState(0);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorStep, setErrorStep] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Состояния для отображения прогресса
  const [pollAttempt, setPollAttempt] = useState(0);
  const [maxPollAttempts] = useState(30);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Добавляем новое состояние для хранения платежных данных
  const [paymentData, setPaymentData] = useState(null);
  
  // Определяем шаги процесса
  const steps = [
    'Проверка платежа',
    'Создание компании',
    'Настройка доступа'
  ];

  // Вспомогательная функция для отправки сообщений через editMessage
  const sendTelegramMessage = async (message) => {
    try {
      await localApi.editMessage(user_id, message);
      console.log(`✅ Отправлено сообщение в Telegram: ${message}`);
    } catch (error) {
      console.error(`❌ Ошибка отправки сообщения в Telegram: ${error.message}`);
    }
  };

  useEffect(() => {
    // Проверяем наличие обязательных параметров
    if (!user_id || !transaction_id) {
      setErrorMessage("Отсутствуют необходимые параметры. Пожалуйста, обратитесь в тех.поддержку.");
      setErrorStep(0);
      setIsLoading(false);
      return;
    }

    // Шаг 1: Проверка транзакции и статуса оплаты
    const checkTransactionStatus = async () => {
      setActiveStep(0);
      try {
        // Если это бесплатное тестирование, пропускаем проверку оплаты
        if (free_trial) {
          console.log("✅ Бесплатное тестирование - пропускаем проверку оплаты");
          await sendTelegramMessage("Пользователь начал бесплатное тестирование");
          
          // Получаем информацию о пользователе
          const userResponse = await localApi.searchUsers({ id: user_id });
          if (!userResponse || userResponse.length === 0) {
            throw new Error("Пользователь не найден.");
          }
          
          const user = userResponse[0];
          
          // Проверяем, создана ли уже компания у пользователя
          if (user.company_is_created === true) {
            // Сохраняем информацию о платеже для отображения
            setPaymentData({
              amount: 0, // Для бесплатного тестирования
              date_last_payment: user.date_last_payment,
              date_next_payment: user.date_next_payment,
              is_trial: true // Флаг бесплатного тестирования
            });
            
            // Отправляем сообщение о бесплатном тестировании существующей компании
            await sendTelegramMessage("Пользователь активировал бесплатное тестирование существующей компании");
            
            // Пропускаем создание компании и переходим к финальному шагу
            setActiveStep(3);
            setIsLoading(false);
            return;
          }
          
          // Переходим к созданию компании
          setActiveStep(1);
        } else {
          // Стандартная проверка для платного режима
          // Получаем последнюю транзакцию пользователя
          const latestTransaction = await localApi.getLatestTransaction(user_id);
          
          // Проверяем, совпадает ли ID последней транзакции с переданным в query
          if (latestTransaction.id !== parseInt(transaction_id)) {
            throw new Error("Неверный идентификатор транзакции.");
          }
          
          // Получаем данные пользователя для проверки статуса компании
          const userResponse = await localApi.searchUsers({ id: user_id });
          
          if (!userResponse || userResponse.length === 0) {
            throw new Error("Пользователь не найден.");
          }
          
          const user = userResponse[0];
          
          // Проверяем, создана ли уже компания у пользователя
          if (user.company_is_created === true) {
            // Сохраняем информацию о платеже для отображения
            setPaymentData({
              amount: latestTransaction.amount,
              date_last_payment: user.date_last_payment,
              date_next_payment: user.date_next_payment,
              is_trial: false // Флаг обычной оплаты
            });
            
            // Отправляем сообщение, что пользователь оплатил доступ к существующей компании
            await sendTelegramMessage("Пользователь оплатил существующую компанию");
            
            // Пропускаем создание компании и переходим к финальному шагу
            setActiveStep(3);
            setIsLoading(false);
            return; // Прерываем выполнение дальнейшего кода
          }
          
          // Проверяем статус транзакции
          let status = latestTransaction.status;
          let attempts = 0;
          
          setTransactionStatus(status);
          
          // Устанавливаем максимальное время ожидания (30 попыток * 2 сек = 60 сек)
          setTimeRemaining(60);
          
          // Если транзакция не в статусе APPROVED, ждем и проверяем повторно
          while (attempts < maxPollAttempts && status !== "APPROVED") {
            setPollAttempt(attempts + 1);
            setTimeRemaining((maxPollAttempts - attempts - 1) * 2);
            
            const transaction = await localApi.getLatestTransaction(user_id);
            status = transaction.status;
            setTransactionStatus(status);
            
            if (status === "APPROVED") {
              break;
            }
            
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Ждем 2 секунды перед следующим запросом
          }
          
          if (status !== "APPROVED") {
            throw new Error("Оплата не была одобрена в течение отведенного времени.");
          }
          
          // Отправляем сообщение, что оплата успешно совершена
          await sendTelegramMessage("Оплата совершена");
          
          // Переходим к следующему шагу
          setActiveStep(1);
        }
      } catch (error) {
        // Отправляем сообщение об ошибке при проверке оплаты
        await sendTelegramMessage(`Ошибка при проверке оплаты: ${error.message}`);
        
        setErrorMessage(`Произошла ошибка при проверке статуса оплаты: ${error.message} Пожалуйста, обратитесь в тех.поддержку.`);
        setErrorStep(0);
        setIsLoading(false);
        return;
      }
      
      // Шаг 2: Создание компании в APOD
      try {
        // Небольшая задержка для визуального отображения процесса
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const userResponse = await localApi.searchUsers({ id: user_id });
        
        if (!userResponse || userResponse.length === 0) {
          throw new Error("Пользователь не найден.");
        }
        
        const user = userResponse[0];
        
        const companyResponse = await localApi.createCompany(
          user.apCompanyName,
          user.tg_username,
          user.name
        );
        
        setCompanyData(companyResponse.data);
        
        // Переход к финальному шагу
        setActiveStep(2);
        
        // Обновляем данные пользователя - устанавливаем разные даты для trial и обычной оплаты
        await localApi.updateUser(user.id, {
          apUUIDcompany: companyResponse.data.id,
          apUUIDuser: companyResponse.data.owner.id,
          date_last_payment: new Date(),
          // Для trial устанавливаем срок 14 дней, для обычной оплаты - 1 месяц
          date_next_payment: free_trial 
            ? new Date(new Date().setDate(new Date().getDate() + 14)) 
            : new Date(new Date().setMonth(new Date().getMonth() + 1)),
          company_is_created: true,
        }); 
        
        // Отправляем сообщение об успешном создании компании
        await sendTelegramMessage(free_trial 
          ? "Бесплатное тестирование активировано, компания создана" 
          : "Оплата совершена, компания создана");
        
        // Завершаем процесс
        await new Promise(resolve => setTimeout(resolve, 800));
        setActiveStep(3);  // Выходим за пределы stepper для обозначения завершения
      } catch (error) {
        // Отправляем сообщение об ошибке при создании компании
        await sendTelegramMessage(`Ошибка при создании компании: ${error.message}`);
        
        setErrorMessage(`Ошибка при создании компании или обновлении данных: ${error.message} Пожалуйста, обратитесь в тех.поддержку.`);
        setErrorStep(1);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkTransactionStatus();
  }, [user_id, transaction_id, maxPollAttempts, free_trial]);

  const redirectToPanel = () => {
    if (paymentData) {
      // Если компания уже существует, перенаправляем на основной сайт
      window.location.href = 'https://apod-lab.tech';
    } else if (companyData) {
      // Если компания только что создана, перенаправляем на поддомен
      window.location.href = `https://${companyData.domain}.apod-lab.tech`;
    }
  };

  const getProgressPercentage = () => {
    return (pollAttempt / maxPollAttempts) * 100;
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return "менее минуты";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    if (mins === 0) return `${secs} сек.`;
    return `${mins} мин. ${secs} сек.`;
  };

  // Функция для форматирования даты в читаемый вид
  const formatDate = (dateString) => {
    if (!dateString) return "Не указана";
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3, mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Обновленный заголовок с логотипом */}
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 4 }}>
          <CompanyLogo />
          <Typography variant="h5" sx={{ ml: 2 }}>
            {free_trial ? "Активация тестового периода" : "Активация аккаунта"}
          </Typography>
        </Box>
        
        {/* Stepper для отображения прогресса */}
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepIcon-root.Mui-active': {
              color: themeColors.primary,
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: themeColors.primary,
            },
            '& .MuiStepLabel-label.Mui-active': {
              color: themeColors.primary,
              fontWeight: 'bold'
            }
          }}
        >
          {steps.map((label, index) => {
            // Изменяем название первого шага для бесплатного тестирования
            const stepLabel = (index === 0 && free_trial) ? 'Проверка данных' : label;
            
            const stepProps = {};
            const labelProps = {};
            
            if (errorStep === index) {
              labelProps.error = true;
            }
            
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{stepLabel}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        
        {/* Содержимое в зависимости от шага */}
        <Box sx={{ mt: 2, mb: 4, minHeight: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {isLoading && activeStep === 0 && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {free_trial ? "Проверяем данные учетной записи..." : "Проверяем статус оплаты..."}
              </Typography>
              
              {!free_trial && pollAttempt > 0 && (
                <>
                  <LinearProgress 
                    variant="determinate" 
                    value={getProgressPercentage()} 
                    sx={{ 
                      mb: 1, 
                      height: 8, 
                      borderRadius: 5,
                      backgroundColor: 'rgba(175, 56, 49, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: themeColors.primary
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Ожидаемое время: {formatTime(timeRemaining)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Статус транзакции: <strong>{transactionStatus || "проверяется"}</strong>
                  </Typography>
                </>
              )}
              
              <CircularProgress size={40} sx={{ mt: 1, color: themeColors.primary }} />
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Создаем вашу компанию в системе APOD...
              </Typography>
              <CircularProgress size={40} sx={{ color: themeColors.primary }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Это займет около 10-15 секунд
              </Typography>
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Настраиваем доступы для вашей компании...
              </Typography>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Осталось совсем немного
              </Typography>
            </Box>
          )}
          
          {activeStep === 3 && !errorMessage && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <CheckCircleOutlineIcon 
                sx={{ fontSize: 60, mb: 2, color: "#af3831" }} 
              />
              
              {paymentData ? (
                // Показываем информацию о существующей компании
                <>
                  <Typography variant="h6" sx={{ mb: 3, color: "#af3831" }}>
                    {paymentData.is_trial 
                      ? "Бесплатное тестирование активировано!" 
                      : "У вас уже есть активная компания!"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {paymentData.is_trial
                      ? "Ваш тестовый период активирован на 14 дней."
                      : "Ваш аккаунт уже активирован и готов к использованию."}
                  </Typography>
                  
                  {/* Информация о платеже/тестовом периоде */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Тип аккаунта: <strong>{paymentData.is_trial ? "Бесплатное тестирование (14 дней)" : "Платный"}</strong>
                    </Typography>
                    
                    {!paymentData.is_trial && paymentData.amount && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Сумма платежа: <strong>{paymentData.amount} ₽</strong>
                      </Typography>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата активации: <strong>{formatDate(paymentData.date_last_payment)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Действует до: <strong>{formatDate(paymentData.date_next_payment)}</strong>
                    </Typography>
                  </Box>
                </>
              ) : (
                // Показываем информацию о новой компании
                <>
                  <Typography variant="h6" sx={{ mb: 3, color: "#af3831" }}>
                    {free_trial 
                      ? "Тестовый период активирован!" 
                      : "Компания успешно создана!"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {free_trial
                      ? "Ваш аккаунт готов к работе. Вы можете бесплатно тестировать систему в течение 14 дней."
                      : "Ваш аккаунт готов к работе. Вы можете перейти в панель управления."}
                  </Typography>
                </>
              )}
              
              <Button 
                variant="contained"
                size="large"
                onClick={redirectToPanel}
                sx={{ 
                  minWidth: 200,
                  bgcolor: '#af3831',
                  '&:hover': { 
                    bgcolor: '#c44940' 
                  },
                  '&:active': {
                    bgcolor: '#963029'
                  }
                }}
              >
                Перейти в панель управления
              </Button>
            </Box>
          )}
          
          {errorMessage && (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <ErrorOutlineIcon sx={{ fontSize: 60, mb: 2, color: "#af3831" }} />
              <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                Произошла ошибка
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {errorMessage}
              </Typography>
              <Button
                variant="outlined"
                href="https://t.me/APOD_lab"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  minWidth: 200, 
                  borderColor: '#af3831', 
                  color: '#af3831',
                  '&:hover': { 
                    borderColor: '#c44940',
                    color: '#c44940',
                    bgcolor: 'rgba(175, 56, 49, 0.04)'
                  }
                }}
              >
                Обратиться в тех.поддержку
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Информация о транзакции для отладки (скрыта в продакшене) */}
        <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #eee", display: "none" }}>
          <Typography variant="caption" color="text.secondary">
            ID пользователя: {user_id || "—"} | ID транзакции: {transaction_id || "—"} | Тестовый режим: {free_trial ? "Да" : "Нет"}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormSuccess;